import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Download, Printer, ArrowUpRight, Mail, Phone, Globe, Presentation } from "lucide-react";
import { toast } from "sonner";
import { trackDownload } from "@/lib/api";
import { DownloadChoices } from "@/components/DownloadChoices";
import { TopBar } from "@/components/TopBar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { ORG, DIRECTORY } from "@/data/tools";
import { printTools, scrollToId } from "@/lib/scroll";
import { QS, QsHealth, QsProvision, QsEhcp, QsParent, QsPlan, QsNotes } from "@/components/quick/QuickPack";
import { ResourceDirectory } from "@/components/tools/ResourceDirectory";
import { QuickFeedbackForm } from "@/components/quick/QuickFeedbackForm";
import { QsCover, QsHeader, QsFooter, QsWelcomePages, QsBackPages, PRIVACY, DISCLAIMER } from "@/components/quick/QuickPrint";

export const QS_INDEX = {
  label: "Quick Start index",
  items: [{ id: "qs-included", short: "What's included" }, ...Object.values(QS).map((t) => ({ id: t.id, n: t.n, short: t.title })), { id: "feedback", short: "Feedback" }],
};
QS_INDEX.ids = QS_INDEX.items.map((i) => i.id);

const POINTS = ["No registration required", "Free download", "Practical templates and checklists", "Designed for immediate use", "Created from lived SEND experience"];
const INCLUDED = [
  { t: "SENDCO Health Check", d: "Review key areas including Leadership, Parent Partnership, SEND Processes, Staff Confidence and Outcomes." },
  { t: "SEND Provision Review", d: "Identify strengths, gaps and priorities for improvement." },
  { t: "EHCP Annual Review Checklist", d: "Stay organised and track essential review requirements." },
  { t: "Parent Partnership Reflection Tool", d: "Evaluate how effectively your school engages with families." },
  { t: "SEND Action Planner", d: "Turn priorities into clear actions and measurable outcomes." },
];
const MORE = [
  { name: "SEND Family Hub", d: "The home of the project", cta: "Visit the Hub", url: DIRECTORY[0].url },
  { name: "12 Toolkit Platform", cta: "Access the Toolkit", url: DIRECTORY[1].url },
  { name: "OAP Toolkit", cta: "Explore the OAP Toolkit", url: DIRECTORY[2].url },
  { name: "Parent SEND Support Website", cta: "Visit Parent Support", url: DIRECTORY[3].url },
  { name: "School Dashboards", d: "Coming soon" },
];
const PERKS = ["Six months' funded access", "Early access to dashboards", "Opportunities to influence development", "Access to future SEND resources"];
const ease = [0.22, 1, 0.36, 1];
const download = () => printTools(null, "printing-qs");
const PDF = "/pdfs/send-quick-start-pack.pdf";
const pdfClick = () => trackDownload("quick-start-pack-pdf");
const go = (id) => (e) => { e.preventDefault(); scrollToId(id); };
const H2 = ({ id, eyebrow, children }) => (
  <>
    <p className="eyebrow mb-4">{eyebrow}</p>
    <h2 id={id} className="font-display text-4xl font-medium leading-[1.05] sm:text-5xl">{children}</h2>
  </>
);

const Hero = () => (
  <section className="pb-20 pt-[150px] lg:pt-[178px]" aria-labelledby="qs-title">
    <div className="mx-auto grid max-w-[1400px] gap-12 px-5 sm:px-8 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,0.75fr)] lg:items-center lg:px-10">
      <div>
        <p className="eyebrow mb-6">Free · No sign-up required</p>
        <h1 id="qs-title" className="font-display text-[2.7rem] font-medium leading-[1] tracking-[-0.03em] sm:text-6xl lg:text-7xl">
          {["Free SENDCO", "Quick Start Pack"].map((l, i) => (
            <span key={l} className="block overflow-hidden pb-[0.08em]">
              <motion.span className={`block ${i === 1 ? "italic text-brand" : ""}`} initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, ease, delay: 0.15 + i * 0.12 }}>{l}</motion.span>
            </span>
          ))}
        </h1>
        <p className="mt-6 font-display text-2xl italic">Practical SEND resources created to support busy schools.</p>
        <p className="mt-5 max-w-xl leading-relaxed text-ink-soft sm:text-lg">Developed by The SEND Accountability Project, this free resource pack provides simple tools to help SENDCOs, Inclusion Leads and school leaders reflect on current provision, identify priorities and plan next steps.</p>
        <DownloadChoices className="mt-8" file="send-quick-start-pack.pdf" resource="quick-start-pack" label="Free SENDCO Quick Start Pack" testid="qs-download"
          onPrint={download} onFillOnline={() => scrollToId("qs-pack")} />
        <Link to="/poster" className="btn btn-ghost mt-4 text-base" data-testid="qs-poster-link"><Presentation size={18} aria-hidden /> Share the poster</Link>
      </div>
      <Reveal className="rounded-[2rem] bg-brand p-7 text-brand-fg sm:p-9">
        <ul className="space-y-4">
          {POINTS.map((t) => <li key={t} className="flex items-center gap-3 text-lg font-bold"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sun text-sun-fg"><Check size={15} strokeWidth={3} aria-hidden /></span>{t}</li>)}
        </ul>
      </Reveal>
    </div>
  </section>
);

const Included = () => (
  <section id="qs-included" className="border-y border-line bg-surface py-20" aria-labelledby="qs-inc">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-10">
      <Reveal><H2 id="qs-inc" eyebrow="What's included?">Five tools. <span className="italic text-brand">Fifteen minutes.</span></H2></Reveal>
      <ol className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {INCLUDED.map((x, i) => (
          <Reveal as="li" key={x.t} delay={i * 0.06} className="rounded-2xl border-2 border-line bg-paper p-5">
            <span className="font-display text-3xl italic text-brand" aria-hidden="true">0{i + 1}</span>
            <p className="mt-2 font-bold">{x.t}</p>
            <p className="mt-1 text-sm text-ink-soft">{x.d}</p>
          </Reveal>
        ))}
      </ol>
      <Reveal className="mt-14 grid gap-8 lg:grid-cols-2">
        <div>
          <H2 id="qs-why" eyebrow="Why we created it">Practical tools, <span className="italic text-brand">less workload.</span></H2>
        </div>
        <div className="text-lg leading-relaxed text-ink-soft">
          <p>The SEND Accountability Project was founded by a parent of three children with SEND.</p>
          <p className="mt-3">Our goal is simple: to provide practical tools, resources and insights that help improve outcomes for children and young people with SEND while reducing unnecessary workload for schools.</p>
        </div>
      </Reveal>
    </div>
  </section>
);

const Pack = () => (
  <section id="qs-pack" className="print-scope py-20" aria-labelledby="qs-pack-title">
    <div className="mx-auto max-w-[1100px] px-5 sm:px-8">
      <div className="no-print mb-10 flex flex-wrap items-end justify-between gap-6">
        <div>
          <H2 id="qs-pack-title" eyebrow="The Quick Start Pack">Fill in. <span className="italic text-brand">Download.</span></H2>
          <p className="mt-3 text-ink-soft">Answers save in this browser only. Do not enter names or anything that could identify a child.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <a href={PDF} download onClick={pdfClick} className="btn btn-brand" data-testid="qs-download-2"><Download size={16} aria-hidden /> Download blank PDF</a>
          <button type="button" onClick={download} className="btn btn-ghost" data-testid="qs-print-2"><Printer size={16} aria-hidden /> Print or save with my answers</button>
        </div>
      </div>
      <table className="print-table">
        <thead><tr><td><QsHeader /></td></tr></thead>
        <tfoot><tr><td><QsFooter /></td></tr></tfoot>
        <tbody><tr><td>
          <QsCover />
          <QsWelcomePages />
          <div className="space-y-10">
            <QsHealth /><QsProvision /><QsEhcp /><QsParent /><QsPlan /><QsNotes />
            <ResourceDirectory id={QS.resources.id} tool={QS.resources} />
          </div>
          <QsBackPages />
        </td></tr></tbody>
      </table>
    </div>
  </section>
);

const More = () => (
  <section className="border-t border-line bg-surface py-20" aria-labelledby="qs-more">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-10">
      <Reveal><H2 id="qs-more" eyebrow="Looking for more?">Explore our growing range of resources.</H2></Reveal>
      <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {MORE.map((m, i) => (
          <Reveal as="li" key={m.name} delay={i * 0.06} className="flex flex-col rounded-2xl border-2 border-line bg-paper p-5">
            <p className="font-display text-xl font-medium">{m.name}</p>
            {m.d && <p className="mt-1 text-sm text-ink-soft">{m.d}</p>}
            {m.url && <a href={`https://${m.url}`} target="_blank" rel="noopener noreferrer" className="mt-auto inline-flex items-center gap-1 pt-4 font-bold text-brand" data-testid={`qs-more-${i}`}>{m.cta} <ArrowUpRight size={16} aria-hidden /><span className="sr-only"> (opens in a new tab)</span></a>}
          </Reveal>
        ))}
      </ul>
      <Reveal className="mt-12 flex flex-col items-start gap-3 rounded-[2rem] border-2 border-ink bg-sun p-7 text-sun-fg sm:flex-row sm:items-center sm:justify-between">
        <div><p className="font-display text-3xl font-medium">Download the pack</p><p className="font-bold">No sign-up required.</p></div>
        <a href={PDF} download onClick={pdfClick} className="btn bg-ink text-paper" data-testid="qs-download-3"><Download size={16} aria-hidden /> Download Free SENDCO Quick Start Pack</a>
      </Reveal>
    </div>
  </section>
);

const Founding = () => (
  <section className="bg-brand py-20 text-brand-fg" aria-labelledby="qs-founding">
    <div className="mx-auto grid max-w-[1400px] gap-10 px-5 sm:px-8 lg:grid-cols-2 lg:px-10">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.22em]">Founding Schools Programme</p>
        <h2 id="qs-founding" className="mt-4 font-display text-5xl font-medium leading-[1] sm:text-6xl">Help shape future <span className="italic text-sun">resources.</span></h2>
        <p className="mt-6 text-lg opacity-90">We're currently inviting schools to help shape future development through our Founding Schools Programme.</p>
        <a href="#feedback" onClick={go("feedback")} className="btn btn-sun mt-8 text-base" data-testid="qs-founding-cta">Express interest</a>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="font-bold">Participants will receive:</p>
        <ul className="mt-4 space-y-3">
          {PERKS.map((p) => <li key={p} className="flex gap-3 rounded-xl p-4 font-bold" style={{ border: "2px solid color-mix(in srgb, currentColor 30%, transparent)" }}><Check size={18} className="mt-0.5 shrink-0 text-sun" aria-hidden />{p}</li>)}
        </ul>
      </Reveal>
    </div>
  </section>
);

const Feedback = () => (
  <section id="feedback" className="py-20" aria-labelledby="qs-fb">
    <div className="mx-auto grid max-w-[1400px] gap-12 px-5 sm:px-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:px-10">
      <Reveal>
        <H2 id="qs-fb" eyebrow="Feedback">We would love <span className="italic text-brand">your feedback.</span></H2>
        <p className="mt-5 text-ink-soft">Your feedback helps us improve future resources.</p>
        <ul className="mt-6 space-y-3 text-sm">
          <li className="flex gap-2.5"><Mail size={18} className="shrink-0" aria-hidden /><a href={`mailto:${ORG.director}`} className="break-all font-bold">{ORG.director}</a></li>
          <li className="flex gap-2.5"><Mail size={18} className="shrink-0" aria-hidden /><a href={`mailto:${ORG.datalead}`} className="break-all font-bold">{ORG.datalead}</a></li>
          <li className="flex gap-2.5"><Phone size={18} className="shrink-0" aria-hidden /><a href={`tel:${ORG.phone.replace(/\s/g, "")}`} className="font-bold">{ORG.phone}</a></li>
          <li className="flex gap-2.5"><Globe size={18} className="shrink-0" aria-hidden /><a href={`https://${ORG.web}`} target="_blank" rel="noopener noreferrer" className="break-all font-bold">{ORG.web}</a></li>
        </ul>
      </Reveal>
      <Reveal delay={0.1} className="rounded-[2rem] border-2 border-line bg-surface p-6 sm:p-10"><QuickFeedbackForm /></Reveal>
    </div>
  </section>
);

const Legal = () => (
  <section className="border-t border-line py-16" aria-label="Privacy notice and disclaimer">
    <div className="mx-auto grid max-w-[1400px] gap-10 px-5 text-sm leading-relaxed sm:px-8 lg:grid-cols-2 lg:px-10">
      <div data-testid="qs-privacy"><h2 className="font-display text-2xl font-medium">Privacy notice</h2>{PRIVACY.map((p) => <p key={p} className="mt-2">{p}</p>)}</div>
      <div data-testid="qs-disclaimer"><h2 className="font-display text-2xl font-medium">Disclaimer</h2>{DISCLAIMER.map((p) => <p key={p} className="mt-2">{p}</p>)}</div>
    </div>
  </section>
);

export default function QuickStartPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const ask = () => toast("Thank you for downloading this resource", {
      description: "Could you spare a minute to leave feedback?",
      duration: 12000,
      action: { label: "Leave feedback", onClick: () => scrollToId("feedback") },
    });
    window.addEventListener("sap:exported", ask);
    const id = window.location.hash.slice(1);
    const t = id ? setTimeout(() => scrollToId(id), 400) : null;
    return () => {
      window.removeEventListener("sap:exported", ask);
      if (t) clearTimeout(t);
    };
  }, []);
  return (
    <>
      <TopBar minimal index={QS_INDEX} />
      <main id="main" tabIndex={-1} className="outline-none">
        <Hero /><Included /><Pack /><More /><Founding /><Feedback /><Legal />
      </main>
      <Footer />
    </>
  );
}
